import { useCallback, useEffect, useRef, useState } from "react"
import { useSession } from "../../auth/session"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { Sku, UserProfile } from "../../api/models"
import Api from "../../api/api"
import { claimRewardThunk, personalOffer } from "../../redux/storeSlice";
import { usePoints } from "../../redux/pointsSlice";
import { AppDispatch } from "../../redux/store";
import PointsBalancePill from "../widgets/PointsBalancePill";
import { UpgradeInfo, CrossgradeInfo, formatPrice } from "../widgets/UpgradeInfo";
import SkuImage from "../widgets/SkuImage";
import SkuName from "../widgets/SkuName";
import OrgChip from "../widgets/OrgChip";
import ChipSelect from "../widgets/ChipSelect";
import Avatar from "../widgets/Avatar";
import { bundleIcon, singleIcon } from "../widgets/pillIcons";

const PAGE_SIZE = 24
const SEARCH_DEBOUNCE_MS = 300
const SEARCH_MIN_CHARS = 2

function StoreCardContent({ sku }: { sku: Sku }) {
  const session = useSession()
  const accessToken = session.accessToken ?? ""
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const { data: points, refresh: refreshPoints } = usePoints(accessToken)
  const [busy, setBusy] = useState(false)
  const [claiming, setClaiming] = useState(false)
  const [upgrading, setUpgrading] = useState(false)

  // the store grid carries one-time, free and reward offerings; the cheapest
  // one-time offering prices the Buy button, a free offering makes the sku
  // claimable at 0, a reward offering makes it claimable for its points cost
  const oneTimeOffering = sku.offerings
    .filter(o => o.paymentType === "one_time")
    .sort((a, b) => a.priceCents - b.priceCents)[0]
  // the one personalized price to show (upgrade or crossgrade, the cheaper);
  // a crossgrade that reached 0 is claimed like a free offering instead
  const offer = personalOffer(sku)
  const upgrade = offer?.kind === "upgrade" ? offer.upgrade : undefined
  const crossgrade = offer?.kind === "crossgrade" && offer.priceCents > 0 ? offer.crossgrade : undefined
  const freeCrossgrade = offer?.kind === "crossgrade" && offer.priceCents === 0
  const freeOffering = sku.offerings.find(o => o.paymentType === "free")
    ?? (freeCrossgrade ? oneTimeOffering : undefined)
  const rewardOffering = sku.offerings
    .filter(o => o.paymentType === "reward")
    .sort((a, b) => a.priceCents - b.priceCents)[0]
  const isFree = freeOffering !== undefined
  const balance = points ? points.topups - points.spendings : 0
  const pointsCost = isFree ? 0 : (rewardOffering?.priceCents ?? 0)
  const claimOffering = freeOffering ?? rewardOffering
  const canClaim = claimOffering !== undefined && (isFree || (!!points && balance >= pointsCost))
  // a running sale: the personal prices above already include it, the plain
  // Buy shows the promoted list price; its id rides along at checkout
  const promotion = sku.promotion

  const handleBuy = async () => {
    if (!oneTimeOffering) return
    setBusy(true)
    try {
      const { checkout } = await Api.makeSkuCheckout({ skuId: sku.id, offeringId: oneTimeOffering.id, redirectUrl: window.location.origin, upgrade: null, promotionId: promotion?.id ?? null, accessToken })
      window.location.href = checkout.attributes.url
    } catch (err) {
      console.error("Checkout failed:", err)
      setBusy(false)
    }
  }

  const handleUpgrade = async () => {
    if (!oneTimeOffering) return
    setUpgrading(true)
    try {
      const { checkout } = await Api.makeSkuCheckout({
        skuId: sku.id,
        offeringId: oneTimeOffering.id,
        redirectUrl: window.location.origin,
        accessToken,
        upgrade: upgrade ?? null,
        crossgrade: crossgrade ? { priceCents: crossgrade.priceCents } : null,
        promotionId: promotion?.id ?? null
      })
      window.location.href = checkout.attributes.url
    } catch (err) {
      console.error("Upgrade checkout failed:", err)
      setUpgrading(false)
    }
  }

  const handleClaim = async () => {
    if (!claimOffering) return
    setClaiming(true)
    try {
      const { pointSpending } = await dispatch(claimRewardThunk({ skuId: sku.id, offeringId: claimOffering.id, accessToken })).unwrap()
      refreshPoints()
      navigate(`/purchase/success?skuId=${sku.id}&rewardClaimId=${pointSpending.id}`)
      return
    } catch (err) {
      console.error("Claim failed:", err)
    }
    setClaiming(false)
  }

  return (
    <div className="store-card">
      <div className="store-card-header">
        <div className="store-card-name"><SkuName sku={sku} variant="section" /></div>
        <div className="store-card-owner">
          by <OrgChip orgId={sku.ownerId} />
        </div>
      </div>
      <SkuImage sku={sku} alt={sku.name} />
      <div className="store-card-body">
        <p className="store-card-desc">{sku.description}</p>
        {sku.highlights.length > 0 && (
          <ul className="tier-features">
            {sku.highlights.map((highlight) => (
              <li key={highlight}>{highlight}</li>
            ))}
          </ul>
        )}
        {promotion && (
          <div className="store-promo">
            <span className="store-promo-rate">{promotion.percentOff}% off</span>
            <span className="store-promo-name">
              {promotion.name ?? "Sale"}{promotion.endsAt ? ` · ends ${new Date(promotion.endsAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : ""}
            </span>
          </div>
        )}
        {upgrade && (
          <>
            <div className="store-card-footer">
              <div className="store-upgrade-info">
                <span className="store-price">{formatPrice(upgrade.priceCents / 100)}</span>
                <UpgradeInfo sku={sku} accessToken={accessToken} />
              </div>
              <button className="btn-buy" onClick={handleUpgrade} disabled={busy || claiming || upgrading}>{upgrading ? "Loading…" : "Upgrade"}</button>
            </div>
            <div className="store-card-or">OR</div>
          </>
        )}
        {crossgrade && (
          <>
            <div className="store-card-footer">
              <div className="store-upgrade-info">
                <span className="store-price">{formatPrice(crossgrade.priceCents / 100)}</span>
                <span className="store-price-was">{formatPrice(crossgrade.listPriceCents / 100)}</span>
                <CrossgradeInfo sku={sku} accessToken={accessToken} />
              </div>
              <button className="btn-buy" onClick={handleUpgrade} disabled={busy || claiming || upgrading}>{upgrading ? "Loading…" : "Buy"}</button>
            </div>
            <div className="store-card-or">OR</div>
          </>
        )}
        {!isFree && oneTimeOffering && (
          <>
            <div className="store-card-footer">
              {promotion ? (
                <div className="store-upgrade-info">
                  <span className="store-price">{formatPrice(promotion.priceCents / 100)}</span>
                  <span className="store-price-was">{formatPrice(oneTimeOffering.priceCents / 100)}</span>
                </div>
              ) : (
                <span className="store-price">{formatPrice(oneTimeOffering.priceCents / 100)}</span>
              )}
              <button className="btn-buy" onClick={handleBuy} disabled={busy || claiming || upgrading}>{busy ? "Loading…" : "Buy"}</button>
            </div>
            {claimOffering !== undefined && <div className="store-card-or">OR</div>}
          </>
        )}
        {claimOffering !== undefined && (
          <div className="store-card-footer">
            <div className="store-upgrade-info">
              <span className="store-points-cost">
                {isFree ? "Free" : `${pointsCost} pts`}
              </span>
              {freeCrossgrade && <CrossgradeInfo sku={sku} accessToken={accessToken} />}
            </div>
            {canClaim && (
              <button className="btn-claim" onClick={handleClaim} disabled={busy || claiming || upgrading}>{claiming ? "Claiming…" : "Claim"}</button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// dollars → integer cents; undefined for empty/invalid input
function parsePriceCents(text: string): number | undefined {
  const value = parseFloat(text)
  if (!Number.isFinite(value) || value < 0) return undefined
  return Math.round(value * 100)
}

// Every criterion is server-side on GET /me/store/skus: ?search=, ?sellerId=,
// ?shape= and the price range (effective price, free = 0). Local state like
// the admin listings.
export default function StorePage() {
  const accessToken = useSession().accessToken ?? ""

  const [searchText, setSearchText] = useState("")
  const [sellerFilter, setSellerFilter] = useState<string>("")
  const [shapeFilter, setShapeFilter] = useState<"" | "bundle" | "single">("")
  const [minPriceText, setMinPriceText] = useState("")
  const [maxPriceText, setMaxPriceText] = useState("")
  const [skus, setSkus] = useState<Sku[] | undefined>(undefined)
  // the manufacturer dropdown's options: every user with a shipped sku,
  // fetched once — complete regardless of which catalog pages are loaded
  const [sellers, setSellers] = useState<UserProfile[]>([])
  const [link, setLink] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  // stale responses (criteria changed mid-flight) must not land in state
  const requestSeq = useRef(0)

  const search = searchText.trim().length >= SEARCH_MIN_CHARS ? searchText.trim() : undefined
  let minPriceCents = parsePriceCents(minPriceText)
  let maxPriceCents = parsePriceCents(maxPriceText)
  // an inverted range is a half-typed one — hold both back until it's valid
  if (minPriceCents !== undefined && maxPriceCents !== undefined && minPriceCents > maxPriceCents) {
    minPriceCents = undefined
    maxPriceCents = undefined
  }
  const sellerId = sellerFilter || undefined
  const shape = shapeFilter || undefined
  const typing = search !== undefined || minPriceText !== "" || maxPriceText !== ""

  // cursor === null loads the first page (replacing state), a cursor appends
  const loadPage = useCallback(async (cursor: string | null) => {
    const seq = ++requestSeq.current
    setLoading(true)
    setError(false)
    try {
      const page = await Api.listStoreSkus({ accessToken, search, sellerId, shape, minPriceCents, maxPriceCents, link: cursor ?? undefined, count: PAGE_SIZE })
      if (seq !== requestSeq.current) return
      setSkus((prev) => cursor === null ? page.skus : [...(prev ?? []), ...page.skus])
      setLink(page.link)
    } catch {
      if (seq === requestSeq.current) setError(true)
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [accessToken, search, sellerId, shape, minPriceCents, maxPriceCents])

  useEffect(() => {
    requestSeq.current++    // discard any in-flight page of the old criteria
    setSkus(undefined)
    setLink(null)
    // debounce typed criteria; dropdown picks load immediately
    const timer = setTimeout(() => loadPage(null), typing ? SEARCH_DEBOUNCE_MS : 0)
    return () => clearTimeout(timer)
  }, [loadPage, typing])

  useEffect(() => {
    let cancelled = false
    Api.listSellers({ accessToken })
      .then(({ sellers }) => { if (!cancelled) setSellers(sellers) })
      .catch(() => { })    // the dropdown just stays empty
    return () => { cancelled = true }
  }, [accessToken])

  const filtersActive = typing || sellerFilter !== "" || shapeFilter !== ""

  return (
    <div className="page">
      <div className="dashboard-header">
        <h1 className="page-title" style={{ margin: 0 }}>Store</h1>
        <PointsBalancePill />
      </div>

      <div className="filter-bar">
        <input
          className="filter-input"
          type="text"
          autoComplete="off"
          placeholder="Search products…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <ChipSelect
          value={sellerFilter}
          onChange={setSellerFilter}
          options={sellers.map((it) => it.id)}
          labels={Object.fromEntries(sellers.map((it) => [it.id, it.id]))}
          icons={Object.fromEntries(sellers.map((it) => [it.id, <Avatar key={it.id} imageUrl={it.imageUrl} size={18} />]))}
          placeholder="All manufacturers"
        />
        <ChipSelect
          value={shapeFilter}
          onChange={setShapeFilter}
          options={["bundle", "single"] as ("bundle" | "single")[]}
          labels={{ bundle: "Bundles", single: "Single product" }}
          icons={{ bundle: bundleIcon, single: singleIcon }}
          placeholder="Bundles & singles"
        />
        <input
          className="filter-input"
          type="number"
          min="0"
          step="0.01"
          placeholder="min $"
          value={minPriceText}
          onChange={(e) => setMinPriceText(e.target.value)}
          style={{ width: 90 }}
        />
        <input
          className="filter-input"
          type="number"
          min="0"
          step="0.01"
          placeholder="max $"
          value={maxPriceText}
          onChange={(e) => setMaxPriceText(e.target.value)}
          style={{ width: 90 }}
        />
      </div>

      {error && skus === undefined && (
        <p className="empty-state">Error loading store</p>
      )}

      {skus === undefined && !error && (
        <p className="empty-state"><span className="spinner" /> Loading store…</p>
      )}

      {skus !== undefined && !error && skus.length === 0 && (
        <p className="empty-state">{filtersActive ? "No products match your criteria" : "The store is empty"}</p>
      )}

      <div className="store-grid">
        {(skus ?? []).map((sku) => (
          <StoreCardContent key={sku.id} sku={sku} />
        ))}
      </div>

      {link !== null && skus !== undefined && (
        loading ? <p className="empty-state"><span className="spinner" /></p> : (
          <button className="card-link" onClick={() => loadPage(link)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, marginTop: 16 }}>
            Load more
          </button>
        )
      )}
      {error && skus !== undefined && (
        <p className="empty-state">Error loading more products</p>
      )}
    </div>
  )
}
